import { type ChangeEvent, useRef, useState } from 'react';
import { type BridgeCamera, useBridgeCameras } from '@/camera/bridgeCameras';
import { useBridgeCamera } from '@/camera/useBridgeCamera';
import { useLocalCamera } from '@/camera/useLocalCamera';
import { samePhotos } from '@/hooks/selectEqual';
import { useRowboat, useSelect } from '@/rowboat';
import { photosForItem } from '@/services/photoService';
import { PhotoGrid } from './PhotoGrid';
import { useModal } from './ui';

/** Copies the picked files and clears the input so the same file can be picked again. */
export function takeFiles(e: ChangeEvent<HTMLInputElement>): File[] {
  // Copy first: clearing the value empties the live FileList.
  const files = Array.from(e.target.files ?? []);
  e.target.value = '';
  return files;
}

type Choice =
  | { kind: 'local'; deviceId: string; label: string }
  | { kind: 'bridge'; camera: BridgeCamera; label: string };

function choiceKey(c: Choice): string {
  return c.kind === 'local' ? c.deviceId : `${c.camera.bridgeId}/${c.camera.cameraId}`;
}

/** Shots and gallery picks keep the camera open, so one visit can photograph an item fully. */
export function CameraView({
  itemId,
  previews,
  saved = [],
  error: captureError = '',
  onFiles,
  onClose,
}: {
  itemId: string;
  previews: Record<string, string>;
  /** Ids of the photos this item's captures saved, oldest first. */
  saved?: string[];
  /** A failed save of a shot or pick; the inspector behind the camera cannot show it. */
  error?: string;
  onFiles: (files: File[]) => void;
  onClose: () => void;
}) {
  const closeRef = useModal(onClose);
  const graph = useRowboat();
  const photos = useSelect(() => photosForItem(graph, itemId), samePhotos);
  const savedAtOpen = useRef(saved.length);
  // Counting this visit's own saves, not rows, leaves out photos synced in from another
  // device; checking them against the item drops any deleted in the viewer since.
  const added = saved
    .slice(savedAtOpen.current)
    .filter((id) => photos.some((p) => p.id === id)).length;
  // Unset until the user switches: the rear camera.
  const [choice, setChoice] = useState<Choice>();
  const onBridge = choice?.kind === 'bridge';
  const local = useLocalCamera(!onBridge, choice?.kind === 'local' ? choice.deviceId : undefined);
  const bridgeCameras = useBridgeCameras();
  const bridge = useBridgeCamera(onBridge ? choice.camera : null, () => void shoot());
  const source = onBridge ? bridge : local;
  const [flash, setFlash] = useState(false);
  const [shotError, setShotError] = useState('');
  const error = shotError || source.error || captureError;

  const choices: Choice[] = [
    ...local.devices.map((d) => ({ kind: 'local' as const, ...d })),
    ...bridgeCameras.map((camera) => ({ kind: 'bridge' as const, camera, label: camera.label })),
  ];
  const currentKey = onBridge ? choiceKey(choice) : local.deviceId;
  const current = choices.findIndex((c) => choiceKey(c) === currentKey);
  const label = onBridge ? choice.label : choices[current]?.label;

  const shoot = async () => {
    if (!source.ready) return;
    setShotError('');
    setFlash(true);
    setTimeout(() => setFlash(false), 150);
    try {
      const blob = await source.capture();
      onFiles([new File([blob], `photo-${Date.now()}.jpg`, { type: blob.type || 'image/jpeg' })]);
    } catch (e) {
      setShotError(e instanceof Error ? e.message : 'Could not take photo');
    }
  };

  const switchCamera = () => {
    setShotError('');
    setChoice(choices[(current + 1) % choices.length]);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Camera"
      className="fixed inset-0 z-40 flex flex-col bg-black text-white"
    >
      <div className="flex items-center justify-between gap-2 px-2 pt-[max(0.25rem,env(safe-area-inset-top))]">
        <button
          ref={closeRef}
          type="button"
          aria-label="Close camera"
          onClick={onClose}
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-xl hover:bg-white/10"
        >
          <span aria-hidden="true">✕</span>
        </button>
        {(onBridge || bridgeCameras.length > 0) && (
          <p data-testid="camera-label" className="min-w-0 truncate text-sm">
            {label}
          </p>
        )}
        <p aria-live="polite" className="pr-3 text-sm">
          {added > 0 && `${added} photo${added === 1 ? '' : 's'} added`}
        </p>
      </div>
      <div className="relative min-h-0 flex-1 basis-0">
        <video
          ref={local.videoRef}
          autoPlay
          playsInline
          muted
          className="absolute inset-0 h-full w-full object-contain"
        />
        {onBridge && bridge.frameUrl && (
          <img
            src={bridge.frameUrl}
            alt="Camera preview"
            className="absolute inset-0 h-full w-full object-contain"
          />
        )}
        {flash && <div aria-hidden="true" className="absolute inset-0 bg-white/70" />}
        {error && (
          <p role="alert" className="absolute inset-x-6 top-1/2 -translate-y-1/2 text-center">
            {error}
          </p>
        )}
        <div className="absolute inset-x-0 bottom-0 grid grid-cols-3 items-center px-6 pb-3">
          <label
            title="Choose from gallery"
            className="relative inline-flex h-12 w-12 cursor-pointer items-center justify-center justify-self-start rounded-xl border border-white/60 bg-black/40 hover:bg-white/10"
          >
            <GalleryIcon />
            <input
              type="file"
              accept="image/*"
              multiple
              aria-label="Choose from gallery"
              onChange={(e) => {
                const files = takeFiles(e);
                if (files.length > 0) onFiles(files);
              }}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </label>
          <button
            type="button"
            aria-label="Shutter"
            disabled={!source.ready}
            onClick={() => void shoot()}
            className="h-16 w-16 justify-self-center rounded-full border-4 border-white bg-white/90 ring-2 ring-black ring-inset transition active:scale-95 disabled:opacity-40"
          />
          {choices.some((_, i) => i !== current) && (
            <button
              type="button"
              aria-label="Switch camera"
              onClick={switchCamera}
              className="inline-flex h-12 w-12 items-center justify-center justify-self-end rounded-full bg-black/40 hover:bg-white/10"
            >
              <SwitchIcon />
            </button>
          )}
        </div>
      </div>
      <div className="min-h-0 flex-1 basis-0 overflow-y-auto border-t border-white/20 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <PhotoGrid itemId={itemId} previews={previews} label="Item photos" highlightNew />
      </div>
    </div>
  );
}

function GalleryIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-6 w-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
    </svg>
  );
}

function SwitchIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-7 w-7"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16M3 21v-5h5" />
    </svg>
  );
}
